/**
 * Job-Specific LLM-Generated Checklist
 *
 * Displays the auto-generated pre-inspection checklist for a job.
 * Allows contractors to check off items and track readiness.
 */

import { useState, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  CheckCircle2,
  Circle,
  AlertTriangle,
  Info,
  AlertOctagon,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Loader2,
  Sparkles,
  Clock,
  Shield,
} from 'lucide-react';
import { CommunityTips } from '@/components/inspection/CommunityTips';
import { cn } from '@/lib/utils';
import type { JobChecklist as JobChecklistType, GeneratedChecklistItem } from '@/types/inspection';

interface JobChecklistProps {
  jobId: string;
  checklist: JobChecklistType | null;
  onChecklistUpdate?: (checklist: JobChecklistType) => void;
  isLoading?: boolean;
}

// Group items by section
function groupBySection(items: GeneratedChecklistItem[]) {
  return items.reduce((acc, item) => {
    const section = item.section || 'General';
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section].push(item);
    return acc;
  }, {} as Record<string, GeneratedChecklistItem[]>);
}

// Severity badge component
function SeverityBadge({ severity }: { severity: 'blocker' | 'warn' | 'info' }) {
  const variants = {
    blocker: { className: 'bg-red-500/10 text-red-500 border-red-500/20', icon: AlertOctagon, label: 'Required' },
    warn: { className: 'bg-amber-500/10 text-amber-500 border-amber-500/20', icon: AlertTriangle, label: 'Important' },
    info: { className: 'bg-blue-500/10 text-blue-500 border-blue-500/20', icon: Info, label: 'Info' },
  };
  const { className, icon: Icon, label } = variants[severity];

  return (
    <Badge variant="outline" className={cn('text-xs gap-1', className)}>
      <Icon className="h-3 w-3" />
      {label}
    </Badge>
  );
}

// Single checklist item component
function ChecklistItemRow({
  item,
  onToggle,
  isUpdating,
}: {
  item: GeneratedChecklistItem;
  onToggle: (itemId: string, isChecked: boolean) => void;
  isUpdating: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={cn(
        'p-3 rounded-lg border transition-colors',
        item.isChecked
          ? 'bg-green-500/5 border-green-500/20'
          : item.severity === 'blocker'
          ? 'bg-red-500/5 border-red-500/20'
          : 'bg-card border-border hover:border-border/80'
      )}
    >
      <div className="flex items-start gap-3">
        <button
          onClick={() => onToggle(item.id, !item.isChecked)}
          disabled={isUpdating}
          className={cn(
            'mt-0.5 flex-shrink-0 transition-colors',
            item.isChecked ? 'text-green-500' : 'text-muted-foreground hover:text-foreground'
          )}
        >
          {isUpdating ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : item.isChecked ? (
            <CheckCircle2 className="h-5 w-5" />
          ) : (
            <Circle className="h-5 w-5" />
          )}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className={cn(
              'text-sm',
              item.isChecked && 'line-through text-muted-foreground'
            )}>
              {item.text}
            </p>
            <SeverityBadge severity={item.severity} />
          </div>

          {(item.rationale || item.codeReference) && (
            <Collapsible open={expanded} onOpenChange={setExpanded}>
              <CollapsibleTrigger asChild>
                <button className="flex items-center gap-1 mt-1 text-xs text-muted-foreground hover:text-foreground">
                  {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  {expanded ? 'Hide details' : 'Show details'}
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 space-y-1">
                {item.rationale && (
                  <p className="text-xs text-muted-foreground">
                    {item.rationale}
                  </p>
                )}
                {item.codeReference && (
                  <p className="text-xs font-mono text-blue-400">
                    Code: {item.codeReference}
                  </p>
                )}
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      </div>
    </div>
  );
}

// Section component
function ChecklistSection({
  section,
  items,
  onToggleItem,
  updatingItems,
  defaultExpanded = true,
}: {
  section: string;
  items: GeneratedChecklistItem[];
  onToggleItem: (itemId: string, isChecked: boolean) => void;
  updatingItems: Set<string>;
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const completedCount = items.filter(i => i.isChecked).length;
  const blockerCount = items.filter(i => i.severity === 'blocker' && !i.isChecked).length;

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded}>
      <CollapsibleTrigger asChild>
        <button className="w-full flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
          <div className="flex items-center gap-2">
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <span className="font-medium">{section}</span>
            <Badge variant="secondary" className="ml-2">
              {completedCount}/{items.length}
            </Badge>
            {blockerCount > 0 && (
              <Badge variant="destructive" className="ml-1">
                {blockerCount} required
              </Badge>
            )}
          </div>
          {completedCount === items.length && (
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          )}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2 space-y-2">
        {items.map((item) => (
          <ChecklistItemRow
            key={item.id}
            item={item}
            onToggle={onToggleItem}
            isUpdating={updatingItems.has(item.id)}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

// Loading skeleton
function ChecklistSkeleton() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-64 mt-2" />
      </CardHeader>
      <CardContent className="space-y-4">
        <Skeleton className="h-2 w-full" />
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Generating state component
function GeneratingState() {
  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardContent className="flex flex-col items-center justify-center py-12">
        <div className="relative">
          <Sparkles className="h-12 w-12 text-primary animate-pulse" />
          <Loader2 className="h-6 w-6 text-primary animate-spin absolute -bottom-1 -right-1" />
        </div>
        <h3 className="mt-4 font-semibold">Generating Your Checklist</h3>
        <p className="mt-1 text-sm text-muted-foreground text-center max-w-sm">
          Our AI is analyzing your job details, jurisdiction requirements, and community intelligence
          to create a personalized pre-inspection checklist.
        </p>
        <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          This usually takes 10-30 seconds
        </div>
      </CardContent>
    </Card>
  );
}

// Empty state - no checklist yet
function EmptyState({ onGenerate, isGenerating }: { onGenerate: () => void; isGenerating: boolean }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-12">
        <Shield className="h-12 w-12 text-muted-foreground" />
        <h3 className="mt-4 font-semibold">No Checklist Yet</h3>
        <p className="mt-1 text-sm text-muted-foreground text-center max-w-sm">
          Generate a personalized pre-inspection checklist based on your job's jurisdiction,
          trade type, and community intelligence.
        </p>
        <Button
          onClick={onGenerate}
          disabled={isGenerating}
          className="mt-4"
        >
          {isGenerating ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4 mr-2" />
              Generate Checklist
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

// Error state
function ErrorState({ error, onRetry, isRetrying }: { error: string; onRetry: () => void; isRetrying: boolean }) {
  return (
    <Card className="border-destructive/20 bg-destructive/5">
      <CardContent className="flex flex-col items-center justify-center py-12">
        <AlertOctagon className="h-12 w-12 text-destructive" />
        <h3 className="mt-4 font-semibold">Generation Failed</h3>
        <p className="mt-1 text-sm text-muted-foreground text-center max-w-sm">
          {error || 'Something went wrong while generating the checklist.'}
        </p>
        <Button
          onClick={onRetry}
          disabled={isRetrying}
          variant="outline"
          className="mt-4"
        >
          {isRetrying ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Retrying...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

// Main component
export function JobChecklist({
  jobId,
  checklist,
  onChecklistUpdate,
  isLoading = false,
}: JobChecklistProps) {
  const { toast } = useToast();
  const [updatingItems, setUpdatingItems] = useState<Set<string>>(new Set());
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);

  // Group items by section
  const groupedItems = useMemo(() => {
    if (!checklist?.items) return {};
    return groupBySection(checklist.items);
  }, [checklist?.items]);

  const sections = Object.keys(groupedItems);

  // Handle item toggle
  const handleToggleItem = async (itemId: string, isChecked: boolean) => {
    if (!checklist) return;

    setUpdatingItems((prev) => new Set(prev).add(itemId));

    try {
      const updateChecklistItem = httpsCallable(functions, 'updateChecklistItem');
      const result = await updateChecklistItem({ jobId, itemId, isChecked });
      const data = result.data as any;

      if (data.success && onChecklistUpdate && checklist) {
        // Update local state
        const updatedItems = checklist.items.map((item) =>
          item.id === itemId ? { ...item, isChecked } : item
        );
        onChecklistUpdate({
          ...checklist,
          items: updatedItems,
          completedItems: data.completedItems,
          readinessScore: data.readinessScore,
          status: data.status,
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update checklist item',
        variant: 'destructive',
      });
    } finally {
      setUpdatingItems((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  };

  // Handle generate checklist
  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const generateChecklist = httpsCallable(functions, 'generateChecklist');
      const result = await generateChecklist({ jobId });
      const data = result.data as any;

      if (data.success) {
        // Fetch the full checklist to update the UI
        const getJobChecklist = httpsCallable(functions, 'getJobChecklist');
        const checklistResult = await getJobChecklist({ jobId });
        const checklistData = checklistResult.data as any;

        if (checklistData.exists && checklistData.checklist && onChecklistUpdate) {
          onChecklistUpdate(checklistData.checklist);
        }

        toast({
          title: 'Checklist Generated',
          description: `${data.checklist.itemCount} items created with ${data.checklist.blockerCount} required items.`,
        });
      }
    } catch (error: any) {
      toast({
        title: 'Generation Failed',
        description: error.message || 'Failed to generate checklist',
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle regenerate checklist
  const handleRegenerate = async () => {
    setShowRegenerateDialog(false);
    setIsRegenerating(true);
    try {
      const regenerateChecklist = httpsCallable(functions, 'regenerateChecklist');
      const result = await regenerateChecklist({ jobId });
      const data = result.data as any;

      if (data.success) {
        // Fetch the full checklist to update the UI
        const getJobChecklist = httpsCallable(functions, 'getJobChecklist');
        const checklistResult = await getJobChecklist({ jobId });
        const checklistData = checklistResult.data as any;

        if (checklistData.exists && checklistData.checklist && onChecklistUpdate) {
          onChecklistUpdate(checklistData.checklist);
        }

        toast({
          title: 'Checklist Regenerated',
          description: `New checklist with ${data.checklist.itemCount} items.`,
        });
      }
    } catch (error: any) {
      toast({
        title: 'Regeneration Failed',
        description: error.message || 'Failed to regenerate checklist',
        variant: 'destructive',
      });
    } finally {
      setIsRegenerating(false);
    }
  };

  // Loading state
  if (isLoading) {
    return <ChecklistSkeleton />;
  }

  // Generating state
  if (checklist?.status === 'generating') {
    return <GeneratingState />;
  }

  // Error state
  if (checklist?.status === 'failed') {
    return (
      <ErrorState
        error={checklist.error || ''}
        onRetry={handleGenerate}
        isRetrying={isGenerating}
      />
    );
  }

  // No checklist yet
  if (!checklist || checklist.status === undefined) {
    return <EmptyState onGenerate={handleGenerate} isGenerating={isGenerating} />;
  }

  // Ready or in progress
  const blockersPending = checklist.items.filter(
    (i) => i.severity === 'blocker' && !i.isChecked
  ).length;
  const progress = checklist.totalItems > 0
    ? Math.round((checklist.completedItems / checklist.totalItems) * 100)
    : 0;

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Pre-Inspection Checklist
              </CardTitle>
              <CardDescription className="mt-1">
                {checklist.metadata?.jurisdictionName} - {checklist.metadata?.trade}
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowRegenerateDialog(true)}
              disabled={isRegenerating}
            >
              {isRegenerating ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-1" />
              )}
              Regenerate
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Progress section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Progress</span>
              <span className="font-medium">
                {checklist.completedItems} / {checklist.totalItems} items
              </span>
            </div>
            <Progress value={progress} className="h-2" />
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">
                {progress}% complete
              </span>
              {blockersPending > 0 ? (
                <span className="text-destructive flex items-center gap-1">
                  <AlertOctagon className="h-3 w-3" />
                  {blockersPending} required items pending
                </span>
              ) : (
                <span className="text-green-500 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  All required items complete
                </span>
              )}
            </div>
          </div>

          {/* Readiness score */}
          {checklist.readinessScore !== null && (
            <div className={cn(
              'p-3 rounded-lg flex items-center justify-between',
              blockersPending === 0 ? 'bg-green-500/10' : 'bg-amber-500/10'
            )}>
              <div className="flex items-center gap-2">
                <Shield className={cn(
                  'h-5 w-5',
                  blockersPending === 0 ? 'text-green-500' : 'text-amber-500'
                )} />
                <span className="font-medium">
                  {blockersPending === 0 ? 'Ready for Inspection' : 'Not Ready'}
                </span>
              </div>
              <Badge variant={blockersPending === 0 ? 'default' : 'secondary'}>
                {checklist.readinessScore}% Readiness
              </Badge>
            </div>
          )}

          {/* Community Tips */}
          {checklist.metadata?.jurisdictionId && (
            <CommunityTips
              jurisdictionId={checklist.metadata.jurisdictionId}
              trade={checklist.metadata.trade}
              context="checklist"
              defaultExpanded={true}
              limit={5}
            />
          )}

          {/* Sections */}
          <div className="space-y-3">
            {sections.map((section, index) => (
              <ChecklistSection
                key={section}
                section={section}
                items={groupedItems[section]}
                onToggleItem={handleToggleItem}
                updatingItems={updatingItems}
                defaultExpanded={index < 3}
              />
            ))}
          </div>

          {/* Metadata footer */}
          <div className="pt-3 border-t text-xs text-muted-foreground space-y-1">
            <p>
              Generated {checklist.generatedAt ? new Date(checklist.generatedAt).toLocaleString() : 'recently'}
            </p>
            {checklist.metadata?.sourcesUsed && (
              <p>
                Sources: {checklist.metadata.sourcesUsed.codeRequirements} code requirements,{' '}
                {checklist.metadata.sourcesUsed.inspectorNotes} inspector notes,{' '}
                {checklist.metadata.sourcesUsed.knownFacts} known facts
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Regenerate confirmation dialog */}
      <AlertDialog open={showRegenerateDialog} onOpenChange={setShowRegenerateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate Checklist?</AlertDialogTitle>
            <AlertDialogDescription>
              This will discard your current checklist progress and generate a new one.
              Your checked items will not be preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRegenerate}>
              Regenerate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
